import React, { SyntheticEvent, useEffect, useState } from "react";
import { Link, Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";
import { Tab, Tabs } from "@mui/material";

import { SuspenseSpinner } from "fond/widgets/SuspenseSpinner";

import { Container, TabContent } from "./common.styles";

type TabDefinition = {
  /**
   * The label to display for this tab. Can be a string or a React node (e.g. for badges).
   */
  label: React.ReactNode;
  /**
   * The path segment for this tab (without leading slash).
   * E.g. "details" for a full path of "/settings/profile/details".
   */
  path: string;
  /**
   * The lazy-loaded component to render for this tab.
   */
  component: React.LazyExoticComponent<React.FC>;
};

export type SettingsTabbedContentProps = {
  /**
   * Optional prefix for the tab links (e.g. "/settings/profile").
   */
  prefix?: string;
  /**
   * The default or "index" path to redirect to if no tab is selected.
   * E.g. "details" to redirect to "/settings/profile/details".
   * If not provided, the first tab in the `tabs` array will be used.
   *
   * Note: This is only used for the `<Route index ... />` element and does not
   * affect the initial selected tab state.
   *
   * Must not start with a leading slash.
   */
  defaultPath?: string;

  tabs: TabDefinition[];
  /**
   * The wrapper for the tab content area. Defaults to `TabContent`.
   */
  ContentContainer?: React.ElementType;
  "data-testid"?: string;
};
function getTabForPath(tabs: TabDefinition[], path: string) {
  return tabs.find((t) => t.path === path) ?? tabs[0];
}

/**
 * A component that allows tabs to be routed, given a set of tabs that have route paths and an optional prefix for those paths.
 */
export const SettingsTabbedContent = ({
  prefix,
  tabs,
  defaultPath = tabs[0].path,
  ContentContainer = TabContent,
  "data-testid": dataTestId,
}: SettingsTabbedContentProps): JSX.Element => {
  const { "*": tab = defaultPath } = useParams<{ "*": string }>();
  const [selectedTab, setSelectedTab] = useState(getTabForPath(tabs, tab));

  /**
   * Ensures a trailing slash on the prefix if provided.
   */
  const pathPrefix = !prefix || prefix.endsWith("/") ? prefix : `${prefix}/`;

  const handleOnTabSelect = (_: SyntheticEvent<Element, Event>, value: string) => {
    setSelectedTab(getTabForPath(tabs, value));
  };

  useEffect(() => {
    setSelectedTab(getTabForPath(tabs, tab));
  }, [tabs, tab]);

  return (
    <Container data-testid={dataTestId}>
      <Tabs onChange={handleOnTabSelect} value={selectedTab.path}>
        {tabs.map(({ label, path }) => (
          <Tab
            value={path}
            key={path}
            component={Link}
            label={label}
            to={`${pathPrefix}${path}`}
            data-testid={`tab-${dataTestId ? `${dataTestId}-` : ""}${path}`}
          />
        ))}
      </Tabs>

      <ContentContainer>
        <SuspenseSpinner>
          <Routes>
            <Route index element={<Navigate to={`${pathPrefix}${defaultPath}`} />} />
            {tabs.map(({ path, component }) => (
              <Route key={path} path={`/${path}`} Component={component} />
            ))}
          </Routes>
          <Outlet />
        </SuspenseSpinner>
      </ContentContainer>
    </Container>
  );
};
